/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Table, Tag, Card, Statistic, Row, Col, Alert } from 'antd';
import {
    ArrowLeftOutlined,
    ShoppingOutlined, DownloadOutlined // NEW: Import download icon
} from '@ant-design/icons';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv'; // NEW: Import CSVLink
import Sidebar from '../../../../components/Sidebar';
import Header from '../../../../components/Header';
import { ActionGetCoupenHistory } from '../../../../store/Actions/CouponAction';

const CouponHistory = () => {
    const { code: couponCode } = useParams();
    const dispatch = useDispatch();

    // Redux state
    const { couponHistory, historyLoader, historyError, totalCount } = useSelector(
        state => state?.CouponManagement
    );

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Build API payload helper
    const buildApiPayload = (filters) => {
        const queryParams = new URLSearchParams();
        
        if (filters.code) queryParams.append('code', filters.code);
        if (filters.page) queryParams.append('page', filters.page);
        if (filters.limit) queryParams.append('limit', filters.limit);

        const queryString = queryParams.toString();

        return {
            Query: queryString ? `?${queryString}` : '',
            obj: {}
        };
    };

    // Fetch history
    const fetchHistory = () => {
        if (!couponCode) return;

        const urlFilters = {
            code: couponCode,
            page: currentPage,
            limit: pageSize
        };
        const apiPayload = buildApiPayload(urlFilters);
        
        dispatch(ActionGetCoupenHistory(apiPayload));
    };

    // Fetch on mount and when couponCode, page, or limit changes
    useEffect(() => {
        fetchHistory();
    }, [couponCode, currentPage, pageSize]);

    // Memoized calculations
    const statistics = useMemo(() => {
        if (!couponHistory || !Array.isArray(couponHistory)) {
            return { totalUses: 0, totalDiscount: 0 };
        }

        return {
            totalUses: couponHistory.length,
            totalDiscount: couponHistory.reduce(
                (sum, item) => sum + (parseFloat(item.discountApplied) || 0),
                0
            ),
        };
    }, [couponHistory]);

    // ✅ NEW: CSV Export Configuration
    const csvHeaders = [
        { label: 'Sr No', key: 'srNo' },
        { label: 'Order ID', key: 'orderUniqueId' },
        { label: 'Customer Name', key: 'customerName' },
        { label: 'Paid Amount (₹)', key: 'paidAmount' },
        { label: 'Discount Applied (₹)', key: 'discount' },
        { label: 'Payment Status', key: 'paymentStatus' },
        { label: 'Order Status', key: 'orderStatus' },
        { label: 'Created Date', key: 'createdAt' },
    ];

    // ✅ NEW: Prepare CSV data with formatting
    const csvData = useMemo(() => {
        if (!Array.isArray(couponHistory) || couponHistory.length === 0) {
            return [];
        }

        return couponHistory.map((item, index) => ({
            srNo: index + 1,
            orderUniqueId: item.orderUniqueId || 'N/A',
            customerName: item.customer.name || 'N/A',
            paidAmount: item.paidAmount ? parseFloat(item.paidAmount).toFixed(2) : '0.00',
            discount: item.discount ? parseFloat(item.discount).toFixed(2) : '0.00',
            paymentStatus: item.paymentStatus || 'N/A',
            orderStatus: item.orderStatus || 'N/A',
            createdDate: item.createdAt ? dayjs(item.createdAt).format('DD MMM YYYY, hh:mm A') : 'N/A',
        }));
    }, [couponHistory]);

    // ✅ NEW: Generate filename with timestamp and coupon code
    const csvFilename = `Coupon_History_${couponCode}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;

    // Payment Status Tag
    const getPaymentStatusTag = (status) => {
        const statusLower = status?.toLowerCase() || '';
        const statusConfig = {
            paid: { color: 'success', text: 'Paid' },
            completed: { color: 'success', text: 'Completed' },
            pending: { color: 'warning', text: 'Pending' },
            failed: { color: 'error', text: 'Failed' },
            cancelled: { color: 'error', text: 'Cancelled' },
        };

        const config = statusConfig[statusLower] || { 
            color: 'default', 
            text: status || 'N/A' 
        };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // Order Status Tag
    const getOrderStatusTag = (status) => {
        const statusLower = status?.toLowerCase() || '';        
        const statusConfig = {
            delivered: { color: 'success', text: 'Delivered' },
            confirmed: { color: 'success', text: 'Confirmed' },
            initialize: { color: 'blue', text: 'Initialize' },
            failed: { color: 'error', text: 'Failed' },
            pending: { color: 'warning', text: 'Pending' },
            cancelled: { color: 'error', text: 'Cancelled' },
            refunded: { color: 'error', text: 'Refunded' },
        };

        const config = statusConfig[statusLower] || { 
            color: 'default', 
            text: status || 'N/A' 
        };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return 'N/A';
        return `₹${parseFloat(amount).toFixed(2)}`;
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return dayjs(date).format('DD MMM YYYY, hh:mm A');
    };

    // Memoized columns
    const columns = useMemo(() => [
        {
            title: 'Sr',
            key: 'sr',
            width: 60,
            render: (text, record, index) => index + 1,
        },
        {
            title: 'Order ID',
            dataIndex: 'orderUniqueId',
            key: 'orderUniqueId',
            render: (orderId) => (
                <span style={{ 
                    fontFamily: 'monospace', 
                    // color: '#1890ff', 
                    color: '#ff6118ff', 
                    fontWeight: 500 
                }}>
                    {orderId || 'N/A'}
                </span>
            ),
        },
     {
            title: 'Customer Name',
            dataIndex: ['customer', 'name'],
            key: 'customerName',
            render: (customerName) => customerName || 'N/A',
        },
        {
            title: 'Paid Amount',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            align: 'right',
            render: (amount) => (
                <span style={{ fontWeight: 500 }}>
                    {formatCurrency(amount)}
                </span>
            ),
        },
        {
            title: 'Discount Applied',
            dataIndex: 'discount',
            key: 'discount',
            align: 'right',
            render: (discount) => (
                <span style={{ fontWeight: 500 }}>
                    {formatCurrency(discount)}
                </span>
            ),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            align: 'center',
            render: (status) => getPaymentStatusTag(status),
        },
        {
            title: 'Order Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            align: 'center',
            render: (status) => getOrderStatusTag(status),
        },
        {
            title: 'Created Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => formatDate(date),
        },
    ], []);

    // Safe array handling
    const tableData = Array.isArray(couponHistory) ? couponHistory : [];

    return (
        <>
            <Header />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/coupon-management">Coupon</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">
                                        Coupon History
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* ✅ UPDATED: Header Actions with CSV Export */}
                    <div className="row mb-3">
                        <div className="col-sm-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={() => window.location.href = '/coupon-management'}
                                    >
                                        Back
                                    </Button>
                                    <h3 className="mb-0">
                                        Coupon History: <Tag color="blue">{couponCode}</Tag>
                                    </h3>
                                </div>
                                
                                {/* ✅ NEW: Export CSV Button */}
                                {tableData.length > 0 ? (
                                    <CSVLink
                                        data={csvData}
                                        headers={csvHeaders}
                                        filename={csvFilename}
                                        className="ant-btn ant-btn-primary"
                                        style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            gap: '8px',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <DownloadOutlined />
                                        Export CSV
                                    </CSVLink>
                                ) : (
                                    <Button
                                        icon={<DownloadOutlined />}
                                        disabled
                                    >
                                        Export CSV
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Error Alert */}
                    {historyError && (
                        <Alert
                            message="Error"
                            description={historyError}
                            type="error"
                            showIcon
                            closable
                            action={
                                <Button size="small" onClick={fetchHistory}>
                                    Retry
                                </Button>
                            }
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* Summary Statistics */}
                    {/* {!historyError && tableData.length > 0 && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Card>
                                    <Statistic
                                        title="Total Uses"
                                        value={statistics.totalUses}
                                        prefix={<ShoppingOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Card>
                                    <Statistic
                                        title="Total Discount Given"
                                        value={statistics.totalDiscount.toFixed(2)}
                                        prefix="₹"
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )} */}

                    {/* History Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    <Table
                                        columns={columns}
                                        dataSource={tableData}
                                        rowKey={(record, index) => 
                                            record.orderId || record._id || index
                                        }
                                        loading={historyLoader}
                                        pagination={{
                                            current: currentPage,
                                            pageSize: pageSize,
                                            total: totalCount || 0,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} records`,
                                            position: ['bottomCenter'],
                                            onChange: (page, size) => {
                                                setCurrentPage(page);
                                                setPageSize(size);
                                            },
                                        }}
                                        locale={{
                                            emptyText: (
                                                <div style={{ 
                                                    padding: '40px', 
                                                    textAlign: 'center' 
                                                }}>
                                                    <ShoppingOutlined 
                                                        style={{ 
                                                            fontSize: 48, 
                                                            color: '#d9d9d9' 
                                                        }} 
                                                    />
                                                    <h4 style={{ marginTop: 16 }}>
                                                        No History Found
                                                    </h4>
                                                    <p style={{ color: '#8c8c8c' }}>
                                                        This coupon has not been used yet.
                                                    </p>
                                                    <Button 
                                                        type="primary" 
                                                        onClick={() => 
                                                            window.location.href = '/coupon-management'
                                                        }
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Back to Coupons
                                                    </Button>
                                                </div>
                                            ),
                                        }}
                                        scroll={{ x: 1200 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CouponHistory;